import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, Home, Loader2, LogIn } from "lucide-react";
import { useEffect, useState, useRef, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  handleCallback,
  redirectToLogin,
} from "@/hooks/services/workchestra/handler/auth";

export const Route = createFileRoute("/callback")({
  component: CallbackComponent,
});

/**
 * Neutral page with a single centred card - the status colour lives only in
 * the icon badge, so an error reads as a message, not a full-screen alarm.
 */
function CallbackCard({
  icon,
  iconBadgeClassName,
  title,
  children,
}: {
  icon: ReactNode;
  iconBadgeClassName: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="aero:flex aero:min-h-screen aero:w-full aero:items-center aero:justify-center aero:bg-neutral-100 aero:p-4">
      <div className="aero:flex aero:w-full aero:max-w-md aero:flex-col aero:items-center aero:gap-4 aero:rounded-[10px] aero:border aero:border-neutral-200 aero:bg-white aero:p-8 aero:text-center aero:shadow-sm">
        <div
          className={cn(
            "aero:flex aero:h-14 aero:w-14 aero:items-center aero:justify-center aero:rounded-lg",
            iconBadgeClassName,
          )}
        >
          {icon}
        </div>
        <h1 className="aero:text-lg aero:font-bold aero:leading-7 aero:text-foreground">
          {title}
        </h1>
        {children}
      </div>
    </div>
  );
}

function CallbackComponent() {
  const [error, setError] = useState<string | null>(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent duplicate processing (React StrictMode in dev mode causes double-invoke)
    if (hasProcessed.current) return;

    const processCallback = async () => {
      hasProcessed.current = true;
      try {
        await handleCallback();
        // Redirect to home page after successful authentication
        window.location.href = "/";
      } catch (err) {
        console.error("Error processing authentication callback:", err);
        setError(err instanceof Error ? err.message : "Authentication failed");
      }
    };

    processCallback();
  }, []);

  if (error) {
    return (
      <CallbackCard
        title="Authentication Error"
        iconBadgeClassName="aero:bg-destructive/10"
        icon={
          <AlertTriangle className="aero:h-7 aero:w-7 aero:text-destructive" />
        }
      >
        <p className="aero:text-sm aero:leading-5 aero:text-neutral-600 aero:break-words">
          {error}
        </p>
        <div className="aero:mt-2 aero:flex aero:w-full aero:flex-col aero:gap-2 aero:sm:flex-row">
          <Button
            intent="neutral"
            design="outline"
            className="aero:flex-1"
            // Full reload rather than router navigation: the failed sign-in
            // leaves no session, so the app should start from a clean state.
            onClick={() => window.location.assign("/")}
          >
            <Home className="aero:h-4 aero:w-4" />
            Return to Home
          </Button>
          <Button
            intent="primary"
            design="solid"
            className="aero:flex-1"
            onClick={redirectToLogin}
          >
            <LogIn className="aero:h-4 aero:w-4" />
            Sign In Again
          </Button>
        </div>
      </CallbackCard>
    );
  }

  return (
    <CallbackCard
      title="Signing You In"
      iconBadgeClassName="aero:bg-brand-primary-50"
      icon={
        <Loader2 className="aero:h-7 aero:w-7 aero:animate-spin aero:text-brand-primary-600" />
      }
    >
      <p
        className="aero:text-sm aero:leading-5 aero:text-neutral-600"
        aria-live="polite"
      >
        Please wait while we complete your sign-in...
      </p>
    </CallbackCard>
  );
}
